import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const RoutineSchema = ({ routine }) => {
  if (!routine) {
    return <Alert severity="info">No routine selected</Alert>;
  }

  const renderParameterType = (param) => {
    let color = 'default';
    if (param.required) color = 'error';
    else if (param.type === 'string') color = 'primary';
    else if (param.type === 'number') color = 'secondary';
    else if (param.type === 'boolean') color = 'success';
    
    return (
      <Chip 
        label={param.type} 
        size="small" 
        color={color}
        variant={param.required ? 'filled' : 'outlined'}
      />
    );
  };

  const renderOutputSchema = (schema, level = 0) => {
    if (!schema || typeof schema !== 'object') {
      return <Typography variant="body2">No schema available</Typography>;
    }

    const indent = level * 20;

    return (
      <Box sx={{ ml: indent }}>
        {schema.type && (
          <Typography variant="body2" color="text.secondary">
            Type: <strong>{schema.type}</strong>
          </Typography>
        )}
        {schema.description && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            {schema.description}
          </Typography>
        )}
        {schema.enum && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Allowed values: {schema.enum.join(', ')}
          </Typography>
        )}
        {schema.properties && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Properties:</Typography>
            {Object.entries(schema.properties).map(([key, value]) => (
              <Box key={key} sx={{ mb: 2, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  {key}
                </Typography>
                {renderOutputSchema(value, level + 1)}
              </Box>
            ))}
          </Box>
        )}
        {schema.items && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Array items:</Typography>
            {renderOutputSchema(schema.items, level + 1)}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {routine.name}
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 2 }}>
        {routine.description}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Chip label={routine.category || 'general'} color="primary" sx={{ mr: 1 }} />
        <Chip label={routine.website} color="secondary" sx={{ mr: 1 }} />
        {routine.tags && routine.tags.map((tag, index) => (
          <Chip key={index} label={tag} variant="outlined" size="small" sx={{ mr: 0.5 }} />
        ))}
      </Box>

      {/* Input Parameters */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            Input Parameters ({routine.input_parameters ? Object.keys(routine.input_parameters).length : 0})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {routine.input_parameters && Object.keys(routine.input_parameters).length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Parameter</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Required</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Example</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(routine.input_parameters).map(([paramName, paramMeta]) => (
                    <TableRow key={paramName}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {paramName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {renderParameterType(paramMeta)}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={paramMeta.required ? 'Yes' : 'No'} 
                          size="small"
                          color={paramMeta.required ? 'error' : 'default'}
                          variant={paramMeta.required ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {paramMeta.description || 'No description'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                          {paramMeta.example || paramMeta.default || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              This routine does not require any input parameters.
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Output Schema */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Expected Output Schema</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {routine.output_schema ? (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              {renderOutputSchema(routine.output_schema)}
            </Box>
          ) : (
            <Alert severity="info">
              No output schema information available for this routine.
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Module Information */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Technical Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Class Name:</strong> {routine.class_name}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Module:</strong> {routine.module}
            </Typography>
            <Typography variant="body2">
              <strong>Target Website:</strong> {routine.website}
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default RoutineSchema;
